import { Injectable } from '@angular/core';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserModel } from '@fi-app/user/user.model';
import { map } from 'rxjs/operators';
import { ObjectTypes } from '@fi-shared/types';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { AppTextService } from '@fi-shared/app-text';
import { UserService } from '@fi-app/user/user.service';
import { IUserModel } from '@fi-shared/models/user.model';
import { FiSignalRHelperService } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import { IUserListItemModel } from '@fi-app/user/user';

@Injectable()
export class UserAccessRequestsService {
    private _userAccessRequests$: BehaviorSubject<IUserListItemModel[]> = new BehaviorSubject<IUserListItemModel[]>([]);
    get userAccessRequests$(): Observable<IUserListItemModel[]> {
        return this._userAccessRequests$.asObservable();
    }
    get userAccessRequestsCount$(): Observable<number> {
        return this.userAccessRequests$.pipe(map((data: IUserListItemModel[]) => data.length));
    }
    get userAccessRequests(): IUserListItemModel[] {
        return this._userAccessRequests$.value;
    }

    constructor(
        private fiBackend: FiBackendService,
        private fiLoadingService: LoadingService,
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private userService: UserService,
        private fiSignalRHelperService: FiSignalRHelperService,
    ) {
        this.userService.userSaved$.subscribe((user: UserModel) => {
            if (user.IsAccessRequesting) {
                this.removeCachedAccessRequests([user.Id]);
            }
        });

        this.fiSignalRHelperService.subscribeToItemUpdated({
            objectType: ObjectTypes.UserAccessRequest,
            fetchItemsFn: (userIds) => {
                const promise = this.getAccessRequests(userIds);
                return promise;
            },
            callback: (result) => {
                this.removeCachedAccessRequests(result.deletedItemIds);
                this.addCachedAccessRequests(result.updatedItems);
            },
            debounceMs: 1000,
        });

        this.fiSignalRHelperService.initialUserAccessRequests.subscribe((data) => this._userAccessRequests$.next(data));
    }

    initForSystemNotifications() {
        //return this.getAccessRequests().then(data => this.userAccessRequests$.next(data));
    }

    private addCachedAccessRequests(newUserAccessRequests: IUserListItemModel[]) {
        const userAccessRequests: IUserListItemModel[] = this._userAccessRequests$.value;
        newUserAccessRequests
            .filter(
                (newUserAccessRequest: IUserListItemModel) =>
                    !userAccessRequests.find(
                        (existingUserAccessRequest: IUserListItemModel) =>
                            existingUserAccessRequest.Id === newUserAccessRequest.Id,
                    ),
            )
            .forEach((newUserAccessRequest: IUserListItemModel) => userAccessRequests.push(newUserAccessRequest));
        this._userAccessRequests$.next(userAccessRequests);
    }

    private removeCachedAccessRequests(userIds: number[]) {
        const userAccessRequests: IUserListItemModel[] = this._userAccessRequests$.value.filter(
            (item: IUserListItemModel) => userIds.indexOf(item.Id) === -1,
        );
        this._userAccessRequests$.next(userAccessRequests);
    }

    private getAccessRequests(userIds: number[]): IFiHttpPromise<IUserListItemModel[]> {
        return this.fiBackend.fetchDataPost<IUserListItemModel[]>('Core/User/GetAccessRequests', userIds);
    }

    saveAccessRequest(user: UserModel): IFiHttpPromise<IUserModel> {
        return this.fiBackend.fetchDataPost<IUserModel>('Core/User/SaveAccessRequest', user);
    }

    private acceptAccessRequest(selectedItems: { Id: number }[]): IFiHttpPromise<void> {
        const ids: number[] = selectedItems.map((user) => user.Id);
        const promise: IFiHttpPromise<void> = this.fiBackend.fetchDataPost<void>('Core/User/AcceptAccessRequest', ids);
        promise.then(() => {
            this.removeCachedAccessRequests(ids);
        });
        return promise;
    }

    rejectAccessRequest(selectedItemIds: number[]): IFiHttpPromise<void> {
        const promise: IFiHttpPromise<void> = this.fiLoadingService.trackPromise(
            this.fiBackend.fetchDataPost<void>('Core/User/RejectAccessRequest', selectedItemIds),
        );
        promise.then(() => {
            this.removeCachedAccessRequests(selectedItemIds);
        });
        return promise;
    }

    acceptSelectedUsersAccessRequests(checkedItems: { Id: number; FullName: string }[]) {
        this.fiLoadingService.trackPromise(this.acceptAccessRequest(checkedItems)).then(() => {
            const messageText: string =
                checkedItems.length > 1
                    ? this.appTextService.get('users_added')
                    : String.format(this.appTextService.get('user_added'), checkedItems[0].FullName);
            // todo: change to snackbar
            this.fiDialogsService.notify({ messageText });
        });
    }
}
